import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useDatabase } from '../context/DatabaseContext';
import DatabaseStats from '../components/dashboard/DatabaseStats';
import CategoryOverview from '../components/dashboard/CategoryOverview';
import TechnicalSpotlight from '../components/dashboard/TechnicalSpotlight';
import TechnicalAnalytics from '../components/dashboard/TechnicalAnalytics';

const { FiSearch, FiDatabase, FiBook } = FiIcons;

const Dashboard = () => {
  const { products, getCategories } = useDatabase();
  const categories = getCategories();

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-gray-900 via-black to-red-900 rounded-lg p-8 text-white mb-8 tech-grid">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">
                Zeak Materialdatenbank
              </h1>
              <p className="text-gray-300 text-lg mb-4">
                Hochpräzisions-Industriekomponenten für professionelle Anwendungen
              </p>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiDatabase} className="w-4 h-4" />
                  <span>{products.length.toLocaleString('de-DE')} Produkte</span>
                </div>
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiDatabase} className="w-4 h-4" />
                  <span>{categories.length} Kategorien</span>
                </div>
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiBook} className="w-4 h-4" />
                  <span>ETIM-Standard</span>
                </div>
              </div>
            </div>
            <div className="flex space-x-4">
              <Link
                to="/search"
                className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center space-x-2 shadow-lg"
              >
                <SafeIcon icon={FiSearch} className="w-5 h-5" />
                <span>Erweiterte Suche</span>
              </Link>
              <Link
                to="/technical-docs"
                className="bg-white/10 backdrop-blur-sm text-white px-6 py-3 rounded-lg hover:bg-white/20 transition-colors font-medium border border-white/20"
              >
                Technische Dokumentation
              </Link>
            </div>
          </div>
        </div>

        {/* Database Statistics */}
        <DatabaseStats products={products} categories={categories} />

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2">
            <CategoryOverview />
          </div>
          <div>
            <TechnicalSpotlight />
          </div>
        </div>

        {/* Technical Analytics */}
        <TechnicalAnalytics />

        {/* Technical Information Footer */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Technische Expertise</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Wälzlagertechnik</h3>
              <p className="text-gray-600 text-sm">
                Punkt- und Linienberührung, Tragzahlen nach ISO 281, Lebensdauerberechnung, Dichtungssysteme.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Lineartechnik</h3>
              <p className="text-gray-600 text-sm">
                Profilschienenführungen, Kugelgewindetriebe, Genauigkeitsklassen, Vorspannungskonzepte.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Dichtungstechnik</h3>
              <p className="text-gray-600 text-sm">
                Materialkompatibilität, Temperaturbeständigkeit, Dynamische und statische Abdichtung.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Antriebstechnik</h3>
              <p className="text-gray-600 text-sm">
                Riementriebe, Kraftübertragung, Kupplungssysteme, Formschluss und Reibschluss.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Dashboard;